import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { THRESHOLD_GROUPS, type ThresholdKey } from '@/app/settingsSchema';

/**
 * 화면 사이에 공유되는 상태 — 원본의 `FFAppState` 짝.
 *
 * ★★ 원본은 전부 `persisted: true` 라 **브라우저에 남는다.** 그래서 여기도
 * `localStorage` 에 저장한다.
 *
 * ⚠️⚠️ **Flutter 쪽에는 그 때문에 탈출 불가 버그가 있었다** — 슬라이더 `max` 를
 * 좁히면 브라우저에 남은 옛 값이 범위를 벗어나 `Slider` 가 assert 로 터지고,
 * 「기본값 복원」 버튼이 **그 죽은 화면에** 있어 빠져나갈 수 없었다.
 * ★ `<input type="range">` 는 범위 밖 값을 조용히 가둬서 그 함정이 없다.
 */

export type Settings = Record<ThresholdKey, number> & {
  /** `mock` | `firestore`. */
  dataSourceMode: string;
  dateRangeKey: string;
  /** 시딩 표본이 첫 실데이터를 묻지 않게 하는 접두(쉼표 구분). */
  excludedRunPrefixes: string;
  soroBaseUrl: string;
  /** 폴백 전용. ⚠️ `cost_usd` 가 한 건이라도 있으면 쓰이지 않는다. */
  costPerTurnKrw: number;
  /**
   * `dark` | `light`.
   *
   * ⚠️⚠️ **이 값은 `index.html` 의 작은 스크립트도 읽는다** — 첫 페인트 전에
   * `data-theme` 을 세워야 **깜빡임이 없다**(React 는 하이드레이션 뒤에야 돈다).
   * ★ 그래서 저장 키(`lens.settings.v1`)와 필드명(`theme`)이 **계약**이다 —
   *   바꾸려면 `index.html` 도 함께 고친다.
   */
  theme: string;
};

/** ★ 슬라이더 표의 `reset` 이 곧 기본값이다 — 두 곳에 적지 않는다. */
export const DEFAULTS: Settings = {
  ...(Object.fromEntries(
    THRESHOLD_GROUPS.flatMap((g) => g.items.map((i) => [i.field, i.reset])),
  ) as Record<ThresholdKey, number>),
  dataSourceMode: 'mock',
  dateRangeKey: '7d',
  excludedRunPrefixes: 'r_,dev_',
  soroBaseUrl: 'https://soro.internal/scene',
  costPerTurnKrw: 25,
  // ★ 기본은 어두운 계기판이다. ⚠️ `index.html` 의 폴백과 같아야 한다.
  theme: 'dark',
};

const KEY = 'lens.settings.v1';

function load(): Settings {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw === null) return DEFAULTS;
    // ⚠️ 저장된 값이 모자라도 **기본값으로 메운다** — 키가 늘었을 때
    //    화면이 `undefined` 를 그리면 안 된다.
    return { ...DEFAULTS, ...(JSON.parse(raw) as Partial<Settings>) };
  } catch {
    return DEFAULTS;
  }
}

export interface SettingsCtx {
  settings: Settings;
  set: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
  /** 기준선만 되돌린다. ★ 데이터 소스·기간은 건드리지 않는다. */
  resetThresholds: () => void;
  /** 화면이 데이터를 다시 읽게 하는 신호. ★ 「다시 불러오기」가 올린다. */
  reloadToken: number;
  reload: () => void;
}

const Ctx = createContext<SettingsCtx | null>(null);

export function AppStateProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<Settings>(load);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(settings));
    } catch {
      // 저장이 막혀도 화면은 돌아야 한다.
    }
  }, [settings]);

  // ★ 테마를 `<html>` 에 반영한다. ⚠️ 첫 페인트는 `index.html` 의 스크립트가
  //   이미 세워 뒀고, 여기는 **그 뒤의 전환**을 맡는다.
  useEffect(() => {
    document.documentElement.dataset['theme'] = settings.theme === 'light' ? 'light' : 'dark';
  }, [settings.theme]);

  const set = useCallback(<K extends keyof Settings>(key: K, value: Settings[K]) => {
    setSettings((s) => ({ ...s, [key]: value }));
  }, []);

  const resetThresholds = useCallback(() => {
    setSettings((s) => {
      const next = { ...s };
      for (const g of THRESHOLD_GROUPS) for (const i of g.items) next[i.field] = i.reset;
      return next;
    });
  }, []);

  const reload = useCallback(() => setReloadToken((n) => n + 1), []);

  const value = useMemo<SettingsCtx>(
    () => ({ settings, set, resetThresholds, reloadToken, reload }),
    [settings, set, resetThresholds, reloadToken, reload],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSettings(): SettingsCtx {
  const v = useContext(Ctx);
  if (v === null) throw new Error('AppStateProvider 안에서만 쓸 수 있습니다.');
  return v;
}

/**
 * 기간 키만 쓰는 화면을 위한 지름길.
 *
 * ⚠️ 원본은 `FFAppState().dateRangeKey` **하나**를 전 화면이 공유한다 —
 * 화면마다 따로 들면 옮길 때마다 기본값으로 돌아가 **같은 기간을 보고 있다고
 * 착각**하게 된다.
 */
export function useAppState(): {
  rangeKey: string;
  setRangeKey: (k: string) => void;
  soroBaseUrl: string;
} {
  const { settings, set } = useSettings();
  return {
    rangeKey: settings.dateRangeKey,
    setRangeKey: (k) => set('dateRangeKey', k),
    soroBaseUrl: settings.soroBaseUrl,
  };
}
